/**
 * CoreVitals dataLayer Implementation
 * Unified tracking structure for page context, product context, and funnel progression
 */

// Initialize dataLayer if it doesn't exist
if (typeof window !== 'undefined') {
  window.dataLayer = window.dataLayer || [];
}

// Declare global types for scan checkout step
declare global {
  interface Window {
    dataLayer: any[];
    scanCheckoutStep?: string;
  }
}

/**
 * Product Type Classification
 * Set at capture time using URL path logic or membership_type
 * 
 * Logic:
 * - If membership_type is provided:
 *   - CORE → 'bloods'
 *   - SCAN → 'scans'
 *   - PLUS → 'both'
 *   - else → 'none'
 * - Otherwise, use URL path logic
 */
export function getProductType(
  path: string,
  membershipType?: string | null
): 'bloods' | 'scan' | 'scans' | 'both' | 'none' {
  // Path-based overrides (take precedence over membership_type)
  if (path.startsWith('/plans/core')) return 'bloods';
  if (path.startsWith('/plans-core')) return 'bloods';
  if (path.startsWith('/upgrade-plan')) return 'bloods';
  if (path.startsWith('/signup')) return 'bloods';
  if (path.startsWith('/full-body-scan')) return 'scans';
  
  // If membership_type is provided, use it to determine product_type
  if (membershipType) {
    const type = membershipType.toUpperCase();
    if (type === 'CORE') return 'bloods';
    if (type === 'SCAN') return 'scans';
    if (type === 'PLUS') return 'both';
    return 'none';
  }

  // Fallback to URL path logic
  // Checkout paths (already handled above, but keeping for completeness)

  if (path.startsWith('/complete-subscription')) {
    const storedType = sessionStorage.getItem('selected_product_type');
    if (storedType === 'bloods') return 'bloods';
    if (storedType === 'scan' || storedType === 'scans') return 'scans';
    return 'none';
  }

  // Microsites
  if (path.startsWith('/bloods-only')) return 'bloods';
  if (path.startsWith('/full-body-scan')) return 'scans';

  return 'none';
}

/**
 * Page Type Classification
 * Checkout is determined purely from path
 */
export function getPageType(path: string): 'home' | 'category_home' | 'info' | 'checkout' | 'other' {
  // Checkout funnel
  const checkoutPaths = [
    '/plans',
    '/plans-core',
    '/upgrade-plan',
    '/signup',
    '/complete-subscription',
    '/full-body-scan',
  ];

  if (checkoutPaths.some(p => path.startsWith(p))) {
    return 'checkout';
  }

  // Home
  if (path === '/') return 'home';

  // Category homes
  if (path === '/bloods-only' || path === '/full-body-scan') {
    return 'category_home';
  }

  // Info pages
  const infoPages = [
    '/bloods-only/how-it-works',
    '/bloods-only/biomarkers',
    '/full-body-scan/the-scan',
    '/full-body-scan/evidence',
    '/full-body-scan/location'
  ];

  if (infoPages.includes(path)) return 'info';

  return 'other';
}

/**
 * Checkout Step Mapping
 */
export function getCheckoutStep(path: string): 'plan' | 'centre' | 'details' | 'payment' | 'none' {
  if (path.startsWith('/plans') || path.startsWith('/plans-core')) return 'plan';
  if (path.startsWith('/upgrade-plan')) return 'plan';
  if (path.startsWith('/signup')) return 'details';

  if (path.startsWith('/complete-subscription')) return 'payment';

  // Scan checkout shared URL
  if (path.startsWith('/full-body-scan')) {
    return (window.scanCheckoutStep as 'centre' | 'details') || 'centre';
  }

  return 'none';
}

/**
 * UTM Capture Helper
 */
export function getUTMs(): {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
} {
  const params = new URLSearchParams(window.location.search);
  const utms: {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
    utm_term?: string;
  } = {};

  const utm_source = params.get('utm_source');
  const utm_medium = params.get('utm_medium');
  const utm_campaign = params.get('utm_campaign');
  const utm_content = params.get('utm_content');
  const utm_term = params.get('utm_term');

  if (utm_source) utms.utm_source = utm_source;
  if (utm_medium) utms.utm_medium = utm_medium;
  if (utm_campaign) utms.utm_campaign = utm_campaign;
  if (utm_content) utms.utm_content = utm_content;
  if (utm_term) utms.utm_term = utm_term;

  return utms;
}

/**
 * Base dataLayer push helper
 */
function pushToDataLayer(eventData: Record<string, any>): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    console.warn('dataLayer not available. Event not pushed:', eventData);
    return;
  }

  const path = window.location.pathname;
  // Use membership_type from eventData if available, otherwise fallback to path logic
  const membershipType = eventData.membership_type || null;
  const product_type = getProductType(path, membershipType);
  const page_type = getPageType(path);
  const checkout_step = getCheckoutStep(path);

  const baseEvent = {
    event: eventData.event,
    url: window.location.href,
    host: window.location.host,
    path,
    product_type,
    page_type,
    checkout_step,
    ...getUTMs(),
    referrer: document.referrer || null,
    ...eventData
  };

  window.dataLayer.push(baseEvent);
}

/**
 * Event 1: active_on_site
 * Browsing and product interest tracking
 * 
 * Fire Trigger:
 * - Page view (web)
 * - Route change (app)
 * - Checkout step change
 * 
 * Deduplication Rules:
 * - Fire only if path OR checkout_step changes
 * - Once per session per path + step
 * - Debounce 2 seconds
 */
let activeOnSiteTimeout: NodeJS.Timeout | null = null;

export function fireActiveOnSite(): void {
  const path = window.location.pathname;
  const checkout_step = getCheckoutStep(path);
  const key = `active_on_site_${path}_${checkout_step}`;

  // Check if already fired for this path + step combination
  if (sessionStorage.getItem(key)) {
    return;
  }

  // Clear existing timeout
  if (activeOnSiteTimeout) {
    clearTimeout(activeOnSiteTimeout);
  }

  // Debounce 2 seconds
  activeOnSiteTimeout = setTimeout(() => {
    sessionStorage.setItem(key, '1');

    pushToDataLayer({
      event: 'active_on_site'
    });
  }, 2000);
}

/**
 * Event 2: subscription_page_view
 * Checkout start detection
 * 
 * Fire Only On:
 * - Bloods: /plans-core
 * - Scan: /full-body-scan
 * 
 * Session Rule:
 * - Once per session per product checkout start
 */
export function fireCheckoutStart(): void {
  const path = window.location.pathname;

  let product_type: 'bloods' | 'scans' | null = null;
  let checkout_step: 'plan' | 'centre' | null = null;

  if (path.startsWith('/plans-core')) {
    product_type = 'bloods';
    checkout_step = 'plan';
    sessionStorage.setItem('selected_product_type', 'bloods');
  }

  if (path.startsWith('/upgrade-plan')) {
    product_type = 'bloods';
    checkout_step = 'plan';
    sessionStorage.setItem('selected_product_type', 'bloods');
  }

  if (path.startsWith('/full-body-scan')) {
    product_type = 'scans';
    checkout_step = 'centre';
    sessionStorage.setItem('selected_product_type', 'scans');
  }

  if (!product_type) return;

  const key = `checkout_start_${product_type}`;
  if (sessionStorage.getItem(key)) return;

  sessionStorage.setItem(key, '1');

  pushToDataLayer({
    event: 'subscription_page_view',
    product_type,
    checkout_step,
    page_type: 'checkout'
  });
}

/**
 * Event 3: form_submitted
 * 
 * A) Site Email Forms
 */
export function fireSiteForm(formId: string, locationName: string): void {
  pushToDataLayer({
    event: 'form_submitted',
    form_kind: 'site_email',
    form_id: formId,
    form_location: locationName,
    checkout_step: 'none'
  });
}

/**
 * Event 3: form_submitted
 * 
 * B) Checkout Details Submitted
 */
export function fireCheckoutDetailsSubmitted(): void {
  pushToDataLayer({
    event: 'form_submitted',
    form_kind: 'checkout_details',
    page_type: 'checkout',
    checkout_step: 'details'
  });
}

/**
 * Get payment product type from sessionStorage
 * Used on payment page to inherit product type
 */
export function getPaymentProductType(): 'bloods' | 'scans' | 'both' | 'none' {
  const stored = sessionStorage.getItem('selected_product_type');
  if (stored === 'bloods') return 'bloods';
  if (stored === 'scans' || stored === 'scan') return 'scans';
  if (stored === 'both') return 'both';
  return 'none';
}

/**
 * Setup scan checkout step listener for iframe postMessage
 * Call this on parent window to listen for step changes from iframe
 */
export function setupScanCheckoutStepListener(): void {
  window.addEventListener('message', (event) => {
    if (event.data?.type !== 'scan_checkout_step') return;

    window.scanCheckoutStep = event.data.step;
    fireActiveOnSite();
  });
}

/**
 * Send scan checkout step signal from iframe
 * Call this from within the scan checkout iframe
 */
export function signalScanCheckoutStep(step: 'centre' | 'details'): void {
  if (window.parent && window.parent !== window) {
    window.parent.postMessage({
      type: 'scan_checkout_step',
      step
    }, '*');
  }
  
  // Also update local window for immediate use
  window.scanCheckoutStep = step;
  fireActiveOnSite();
}

/**
 * Map planType (like 'coreVitals', 'coreVitalsPlus', 'fullBody') to membership_type ('CORE', 'PLUS', 'SCAN')
 */
function mapPlanTypeToMembershipType(planType: string): string | null {
  if (!planType) return null;
  
  const type = planType.toLowerCase();
  if (type === 'corevitals') return 'CORE';
  if (type === 'corevitalsplus') return 'PLUS';
  if (type === 'fullbody') return 'SCAN';
  
  return null;
}

/**
 * Event 4: plan_change
 * Track when user changes plan selection or test count
 */
export function firePlanChange(
  planType: string,
  testCount?: number | null,
  paymentType?: 'monthly' | 'yearly',
  product?: { plan_type?: string } | null
): void {
  const path = window.location.pathname;
  
  // Special case: if plan_type is fullbody, product_type should be 'scan' (singular)
  const planTypeLower = planType.toLowerCase();
  let product_type: 'bloods' | 'scan' | 'scans' | 'both' | 'none';
  
  if (planTypeLower === 'fullbody') {
    product_type = 'scan';
  } else {
    // Determine membership_type: prefer product.plan_type, otherwise map from planType
    const membershipType = product?.plan_type || mapPlanTypeToMembershipType(planType);
    // Get product_type based on membership_type
    product_type = getProductType(path, membershipType);
  }
  
  // Determine membership_type for the event (still needed for other tracking)
  const membershipType = product?.plan_type || mapPlanTypeToMembershipType(planType);
  
  pushToDataLayer({
    event: 'plan_change',
    plan_type: planType,
    test_count: testCount || null,
    payment_type: paymentType || null,
    membership_type: membershipType,
    product_type: product_type,
    page_type: getPageType(path),
    checkout_step: getCheckoutStep(path)
  });
}

/**
 * Event 5: payment_type_change
 * Track when user switches between monthly and yearly payment
 */
export function firePaymentTypeChange(
  paymentType: 'monthly' | 'yearly',
  planType?: string,
  testCount?: number | null
): void {
  pushToDataLayer({
    event: 'payment_type_change',
    payment_type: paymentType,
    plan_type: planType || null,
    test_count: testCount || null
  });
}

/**
 * Event 6: discount_applied
 * Track when a discount code is successfully applied
 */
export function fireDiscountApplied(discountCode: string): void {
  pushToDataLayer({
    event: 'discount_applied',
    discount_code: discountCode,
    checkout_step: getCheckoutStep(window.location.pathname)
  });
}

/**
 * Event 7: discount_removed
 * Track when a discount code is removed
 */
export function fireDiscountRemoved(discountCode?: string): void {
  pushToDataLayer({
    event: 'discount_removed',
    discount_code: discountCode || null,
    checkout_step: getCheckoutStep(window.location.pathname)
  });
}

/**
 * Clear deduplication keys (useful for testing)
 */
export function clearDeduplicationKeys(): void {
  const keys = Object.keys(sessionStorage);
  keys.forEach(key => {
    if (key.startsWith('active_on_site_') || key.startsWith('checkout_start_')) {
      sessionStorage.removeItem(key);
    }
  });
}
